import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Calendar as CalendarWidget } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import EntityHistory from "@/components/EntityHistory";
import { format } from "date-fns";
import {
  Package, Lock, Plus, RefreshCw, Search, Trash2, X,
  ChevronRight, ChevronLeft, Archive, ArchiveRestore,
  Calendar, FileText, IndianRupee, CheckCircle, Clock,
  AlertTriangle, Users, ClipboardList, Unlock, Eye,
  ChevronDown as ChDown, ChevronUp as ChUp,
  CalendarIcon, CalendarRange, ArrowRight, Mail, Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const fmt = (v) => {
  if (v >= 100000) return `\u20B9${(v / 100000).toFixed(2)}L`;
  if (v >= 1000) return `\u20B9${(v / 1000).toFixed(1)}K`;
  return `\u20B9${Math.round(v).toLocaleString("en-IN")}`;
};
const fmtFull = (v) => `\u20B9${Math.round(v).toLocaleString("en-IN")}`;
const fmtMonth = (m) => {
  if (!m) return "";
  const [y, mo] = m.split("-");
  return `${MONTH_NAMES[parseInt(mo) - 1] || mo} ${y}`;
};

const InvoiceBatches = () => {
  const [activeTab, setActiveTab] = useState("regular"); // regular | mini
  const [loading, setLoading] = useState(true);
  const [batches, setBatches] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [archivedGroups, setArchivedGroups] = useState([]);
  const [view, setView] = useState("list");
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchDetail, setBatchDetail] = useState(null);

  // Create batch state
  const [createMonth, setCreateMonth] = useState("");
  const [createName, setCreateName] = useState("");
  const [billingMonth, setBillingMonth] = useState("");
  const [episodeTree, setEpisodeTree] = useState([]);
  const [fullyBatched, setFullyBatched] = useState([]);
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
  const [expandedTitles, setExpandedTitles] = useState(new Set());
  const [expandedSeasons, setExpandedSeasons] = useState(new Set());
  const [treeSearch, setTreeSearch] = useState("");
  const [loadingTree, setLoadingTree] = useState(false);
  const [saving, setSaving] = useState(false);

  // Selection mode: "single" | "range" | "dates"
  const [selectionMode, setSelectionMode] = useState("single");
  const [rangeFromMonth, setRangeFromMonth] = useState("");
  const [rangeToMonth, setRangeToMonth] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [treeSortBy, setTreeSortBy] = useState("amount"); // "amount" | "due_date" | "name" | "tasks"

  // Preview translator state
  const [showPreview, setShowPreview] = useState(false);
  const [previewEmail, setPreviewEmail] = useState("");
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [batchResources, setBatchResources] = useState([]);

  // Mini batch state
  const [miniBatches, setMiniBatches] = useState([]);
  const [miniResources, setMiniResources] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState(new Set());
  const [miniName, setMiniName] = useState("");
  const [miniSearch, setMiniSearch] = useState("");
  const [miniView, setMiniView] = useState("list"); // list | create | detail
  const [miniDetail, setMiniDetail] = useState(null);
  const [miniLoading, setMiniLoading] = useState(true);

  // Confirm dialogs
  const [showLockConfirm, setShowLockConfirm] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Add tasks to batch dialog
  const [showAddTasks, setShowAddTasks] = useState(false);
  const [addTree, setAddTree] = useState([]);
  const [addTreeLoading, setAddTreeLoading] = useState(false);
  const [addSelectedIds, setAddSelectedIds] = useState(new Set());
  const [addSearch, setAddSearch] = useState("");
  const [addExpandedTitles, setAddExpandedTitles] = useState(new Set());
  const [addExpandedSeasons, setAddExpandedSeasons] = useState(new Set());
  const [addSortBy, setAddSortBy] = useState("amount");

  // Email notifications
  const [sendingEmails, setSendingEmails] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  // Batch detail tasks
  const [detailTasks, setDetailTasks] = useState([]);
  const [selectedDetailIds, setSelectedDetailIds] = useState(new Set());
  const [removingBulk, setRemovingBulk] = useState(false);
  const [resourceFilter, setResourceFilter] = useState("");
  const [batchTab, setBatchTab] = useState("tasks");
  const [resourceSearch, setResourceSearch] = useState("");
  const [billSort, setBillSort] = useState("amount_desc");
  const [detailTotal, setDetailTotal] = useState(0);
  const [detailPage, setDetailPage] = useState(1);
  const [detailPages, setDetailPages] = useState(1);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailSearch, setDetailSearch] = useState("");
  const [detailTaskType, setDetailTaskType] = useState("");
  const [detailSortBy, setDetailSortBy] = useState("due_date");
  const [detailSortOrder, setDetailSortOrder] = useState("desc");
  const [detailFilterOpts, setDetailFilterOpts] = useState({ task_types: [] });

  // ======== REGULAR BATCH METHODS ========
  const fetchBatches = useCallback(async () => {
    try {
      const [batchRes, monthRes] = await Promise.all([
        axios.get(`${API}/batches`),
        axios.get(`${API}/batch-available-months`),
      ]);
      setBatches(batchRes.data.batches || []);
      setAvailableMonths(monthRes.data.months || []);
    } catch {
      toast.error("Failed to load batches");
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    await fetchBatches();
    setLoading(false);
  }, [fetchBatches]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Fetch detail tasks when entering detail view or filters change
  useEffect(() => {
    if (view === "detail" && selectedBatch) {
      fetchDetailTasks(selectedBatch, 1);
    }
  }, [view, selectedBatch, detailSearch, detailTaskType, detailSortBy, detailSortOrder]);

  const fetchEpisodeTree = async (params) => {
    setLoadingTree(true);
    try {
      const qp = new URLSearchParams(params);
      const res = await axios.get(`${API}/batch-episode-tree?${qp}`);
      setEpisodeTree(res.data.tree || []);
      setFullyBatched(res.data.fully_batched || []);
    } catch { toast.error("Failed to load episode tree"); }
    finally { setLoadingTree(false); }
  };

  const fetchBatchDetail = async (batchId) => {
    try {
      const res = await axios.get(`${API}/batches/${batchId}`);
      setBatchDetail(res.data);
      setBatchResources(res.data.resources || []);
    } catch { toast.error("Failed to load batch details"); }
  };

  const fetchDetailTasks = useCallback(async (batchId, pg = 1) => {
    if (!batchId) return;
    setDetailLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: 50, sort_by: detailSortBy, sort_order: detailSortOrder });
      if (detailSearch) params.set("search", detailSearch);
      if (detailTaskType) params.set("task_type", detailTaskType);
      if (resourceFilter) params.set("resource", resourceFilter);
      const res = await axios.get(`${API}/batches/${batchId}/tasks?${params}`);
      setDetailTasks(res.data.tasks || []);
      setDetailTotal(res.data.total || 0);
      setDetailPages(res.data.pages || 1);
      setDetailPage(pg);
      setDetailFilterOpts(res.data.filters || { task_types: [] });
    } catch { toast.error("Failed to load tasks"); }
    finally { setDetailLoading(false); }
  }, [detailSearch, detailTaskType, detailSortBy, detailSortOrder, resourceFilter]);

  const removeTaskFromBatch = async (taskId) => {
    if (!selectedBatch) return;
    try {
      await axios.post(`${API}/batches/${selectedBatch}/remove-tasks`, { task_ids: [taskId] });
      toast.success("Task removed from batch");
      setSelectedDetailIds(prev => { const n = new Set(prev); n.delete(taskId); return n; });
      fetchDetailTasks(selectedBatch, detailPage);
      fetchBatchDetail(selectedBatch);
    } catch (err) { toast.error(err.response?.data?.detail || "Remove failed"); }
  };

  const removeBulkTasks = async () => {
    if (!selectedBatch || selectedDetailIds.size === 0) return;
    if (!window.confirm(`Remove ${selectedDetailIds.size} selected tasks from this batch?`)) return;
    setRemovingBulk(true);
    try {
      await axios.post(`${API}/batches/${selectedBatch}/remove-tasks`, { task_ids: Array.from(selectedDetailIds) });
      toast.success(`${selectedDetailIds.size} tasks removed`);
      setSelectedDetailIds(new Set());
      fetchDetailTasks(selectedBatch, detailPage);
      fetchBatchDetail(selectedBatch);
    } catch (err) { toast.error(err.response?.data?.detail || "Remove failed"); }
    finally { setRemovingBulk(false); }
  };

  const toggleDetailSelect = (id) => setSelectedDetailIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const selectAllDetail = () => { if (selectedDetailIds.size === detailTasks.length) setSelectedDetailIds(new Set()); else setSelectedDetailIds(new Set(detailTasks.map(t => t.id))); };

  const toggleDetailSort = (field) => {
    if (detailSortBy === field) setDetailSortOrder(p => p === "asc" ? "desc" : "asc");
    else { setDetailSortBy(field); setDetailSortOrder("desc"); }
  };

  // Send email notifications
  const handleSendNotifications = async () => {
    if (!selectedBatch || !window.confirm("Send email notifications to all resources in this batch?")) return;
    setSendingEmails(true);
    try {
      const res = await axios.post(`${API}/batches/${selectedBatch}/send-notifications`);
      setEmailResult(res.data);
      toast.success(`Emails sent: ${res.data.sent_count} succeeded, ${res.data.failed_count} failed`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send notifications"); }
    finally { setSendingEmails(false); }
  };

  // Add tasks to batch
  const openAddTasks = async () => {
    setShowAddTasks(true);
    setAddSelectedIds(new Set());
    setAddSearch("");
    setAddExpandedTitles(new Set());
    setAddExpandedSeasons(new Set());
    setAddSortBy("amount");
    setAddTreeLoading(true);
    try {
      const res = await axios.get(`${API}/batch-episode-tree?from_month=2025-01&to_month=2026-12`);
      setAddTree(res.data.tree || []);
    } catch { toast.error("Failed to load tasks"); }
    finally { setAddTreeLoading(false); }
  };

  const handleAddTasks = async () => {
    if (addSelectedIds.size === 0 || !selectedBatch) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/batches/${selectedBatch}/add-tasks`, { task_ids: Array.from(addSelectedIds) });
      toast.success(`Added ${res.data.added} tasks (total: ${res.data.total})`);
      setShowAddTasks(false);
      fetchBatchDetail(selectedBatch);
      fetchDetailTasks(selectedBatch, 1);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add tasks"); }
    finally { setSaving(false); }
  };

  // Refetch tasks when resource filter changes
  useEffect(() => {
    if (selectedBatch) fetchDetailTasks(selectedBatch, 1);
  }, [resourceFilter]);

  const filteredAddTree = useMemo(() => {
    let tree = addTree;
    if (addSearch) {
      const q = addSearch.toLowerCase();
      tree = tree.filter(t => t.main_title.toLowerCase().includes(q) || t.seasons.some(s => s.episodes.some(e => (e.episode_title || "").toLowerCase().includes(q))));
    }
    const sorted = [...tree];
    if (addSortBy === "due_date") sorted.sort((a, b) => (a.earliest_due || "").localeCompare(b.earliest_due || ""));
    else if (addSortBy === "name") sorted.sort((a, b) => a.main_title.localeCompare(b.main_title));
    else if (addSortBy === "tasks") sorted.sort((a, b) => b.task_count - a.task_count);
    else sorted.sort((a, b) => b.total_amount - a.total_amount);
    return sorted;
  }, [addTree, addSearch, addSortBy]);

  const addToggleTitle = (title) => setAddExpandedTitles(prev => { const n = new Set(prev); if (n.has(title)) n.delete(title); else n.add(title); return n; });
  const addToggleSeason = (key) => setAddExpandedSeasons(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const addToggleIds = (ids) => {
    setAddSelectedIds(prev => {
      const next = new Set(prev);
      const allSelected = ids.every(id => next.has(id));
      if (allSelected) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  };

  const fetchArchived = async () => {
    try {
      const res = await axios.get(`${API}/archived-tasks`);
      setArchivedGroups(res.data.archived_groups || []);
    } catch { toast.error("Failed to load archived tasks"); }
  };

  const startCreate = () => { setView("create"); setSelectionMode("single"); setCreateMonth(""); setCreateName(""); setBillingMonth(""); setEpisodeTree([]); setSelectedTaskIds(new Set()); setTreeSearch(""); setExpandedTitles(new Set()); setExpandedSeasons(new Set()); setRangeFromMonth(""); setRangeToMonth(""); setDateFrom(""); setDateTo(""); setTreeSortBy("amount"); };

  const onMonthSelect = (month) => { setCreateMonth(month); setCreateName(fmtMonth(month)); if (!billingMonth) setBillingMonth(month); fetchEpisodeTree({ month }); setSelectedTaskIds(new Set()); setExpandedTitles(new Set()); setExpandedSeasons(new Set()); };

  const onMonthRangeApply = () => {
    if (!rangeFromMonth || !rangeToMonth) return;
    setCreateName(`${fmtMonth(rangeFromMonth)} — ${fmtMonth(rangeToMonth)}`);
    if (!billingMonth) setBillingMonth(rangeFromMonth);
    setCreateMonth(rangeFromMonth);
    fetchEpisodeTree({ from_month: rangeFromMonth, to_month: rangeToMonth });
    setSelectedTaskIds(new Set()); setExpandedTitles(new Set()); setExpandedSeasons(new Set());
  };

  const onDateRangeApply = () => {
    if (!dateFrom || !dateTo) return;
    setCreateName(`${dateFrom} to ${dateTo}`);
    const monthPart = dateFrom.substring(0, 7);
    if (!billingMonth) setBillingMonth(monthPart);
    setCreateMonth(monthPart);
    fetchEpisodeTree({ from_date: dateFrom, to_date: dateTo });
    setSelectedTaskIds(new Set()); setExpandedTitles(new Set()); setExpandedSeasons(new Set());
  };

  // Tree selection helpers
  const toggleTaskIds = (ids, forceSelect) => {
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      const allSelected = forceSelect !== undefined ? !forceSelect : ids.every(id => next.has(id));
      if (allSelected) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  };

  const toggleTitle = (title) => setExpandedTitles(prev => { const n = new Set(prev); if (n.has(title)) n.delete(title); else n.add(title); return n; });
  const toggleSeason = (key) => setExpandedSeasons(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });

  const filteredTree = useMemo(() => {
    let tree = episodeTree;
    if (treeSearch) {
      const q = treeSearch.toLowerCase();
      tree = tree.filter(t => t.main_title.toLowerCase().includes(q) || t.seasons.some(s => s.episodes.some(e => (e.episode_title || "").toLowerCase().includes(q))));
    }
    // Sort
    const sorted = [...tree];
    if (treeSortBy === "due_date") sorted.sort((a, b) => (a.earliest_due || "").localeCompare(b.earliest_due || ""));
    else if (treeSortBy === "name") sorted.sort((a, b) => a.main_title.localeCompare(b.main_title));
    else if (treeSortBy === "tasks") sorted.sort((a, b) => b.task_count - a.task_count);
    else sorted.sort((a, b) => b.total_amount - a.total_amount);
    return sorted;
  }, [episodeTree, treeSearch, treeSortBy]);

  const selectedStats = useMemo(() => {
    let tasks = 0, amount = 0, duration = 0;
    episodeTree.forEach(t => t.seasons.forEach(s => s.episodes.forEach(e => e.tasks.forEach(tk => {
      if (selectedTaskIds.has(tk.id)) { tasks++; amount += tk.amount || 0; duration += tk.duration_words || 0; }
    }))));
    return { tasks, amount, duration };
  }, [episodeTree, selectedTaskIds]);

  // Generate billing month options (last 24 months)
  const billingMonthOptions = useMemo(() => {
    const opts = [];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      opts.push({ value: val, label: fmtMonth(val) });
    }
    return opts;
  }, []);

  const saveBatch = async () => {
    if (selectedTaskIds.size === 0) { toast.error("Select at least one task"); return; }
    if (!billingMonth) { toast.error("Select a billing month (Financial Year)"); return; }
    const batchMonth = createMonth || billingMonth;
    setSaving(true);
    try {
      await axios.post(`${API}/batches`, { name: createName || "Batch", month: batchMonth, billing_month: billingMonth, selected_projects: [], selected_task_ids: Array.from(selectedTaskIds) });
      toast.success("Batch created");
      await fetchBatches();
      setView("list");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create batch"); }
    finally { setSaving(false); }
  };

  const lockBatch = async () => {
    if (!selectedBatch) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/batches/${selectedBatch}/lock`);
      toast.success(`Batch locked with ${res.data.locked_tasks} tasks`);
      setShowLockConfirm(false);
      await fetchBatches();
      await fetchBatchDetail(selectedBatch);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to lock batch"); }
    finally { setSaving(false); }
  };

  const archiveUnselected = async () => {
    if (!selectedBatch) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/batches/${selectedBatch}/archive-unselected`);
      toast.success(`Archived ${res.data.archived_count} tasks`);
      setShowArchiveConfirm(false);
      await fetchBatches();
    } catch { toast.error("Failed to archive tasks"); }
    finally { setSaving(false); }
  };

  const deleteBatch = async () => {
    if (!selectedBatch) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/batches/${selectedBatch}`);
      toast.success("Batch deleted");
      setShowDeleteConfirm(false);
      setSelectedBatch(null); setBatchDetail(null); setView("list");
      await fetchBatches();
    } catch { toast.error("Failed to delete batch"); }
    finally { setSaving(false); }
  };

  const unarchiveTasks = async (taskIds) => {
    try {
      const res = await axios.post(`${API}/tasks/unarchive`, { task_ids: taskIds });
      toast.success(`Unarchived ${res.data.unarchived_count} tasks`);
      await fetchArchived();
      await fetchBatches();
    } catch { toast.error("Failed to unarchive tasks"); }
  };

  // Unlock batch
  const unlockBatch = async () => {
    if (!selectedBatch) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/batches/${selectedBatch}/unlock`);
      toast.success(`Unlocked ${res.data.unlocked_tasks} tasks`);
      setShowLockConfirm(false);
      await fetchBatches();
      await fetchBatchDetail(selectedBatch);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to unlock"); }
    finally { setSaving(false); }
  };

  // Preview translator
  const previewTranslator = async (email) => {
    setPreviewLoading(true);
    try {
      const url = selectedBatch
        ? `${API}/admin/preview-translator/${encodeURIComponent(email)}?batch_id=${selectedBatch}`
        : `${API}/admin/preview-translator/${encodeURIComponent(email)}`;
      const res = await axios.get(url);
      setPreviewData(res.data);
      setPreviewEmail(email);
      setShowPreview(true);
    } catch { toast.error("Failed to load translator preview"); }
    finally { setPreviewLoading(false); }
  };

  // ======== MINI BATCH METHODS ========
  const fetchMiniBatches = useCallback(async () => {
    setMiniLoading(true);
    try {
      const [bRes, rRes] = await Promise.all([
        axios.get(`${API}/mini-batches`),
        axios.get(`${API}/mini-batch-resources`),
      ]);
      setMiniBatches(bRes.data.batches || []);
      setMiniResources(rRes.data.resources || []);
    } catch { toast.error("Failed to load mini batches"); }
    finally { setMiniLoading(false); }
  }, []);

  useEffect(() => { if (activeTab === "mini") fetchMiniBatches(); }, [activeTab, fetchMiniBatches]);

  const filteredMiniResources = useMemo(() => {
    if (!miniSearch) return miniResources;
    const q = miniSearch.toLowerCase();
    return miniResources.filter(r => r.email.toLowerCase().includes(q) || (r.display_name || "").toLowerCase().includes(q));
  }, [miniResources, miniSearch]);

  const toggleEmail = (email) => {
    setSelectedEmails(prev => { const next = new Set(prev); if (next.has(email)) next.delete(email); else next.add(email); return next; });
  };

  const toggleAllEmails = () => {
    const visible = filteredMiniResources.map(r => r.email);
    if (visible.every(e => selectedEmails.has(e))) {
      setSelectedEmails(prev => { const next = new Set(prev); visible.forEach(e => next.delete(e)); return next; });
    } else {
      setSelectedEmails(prev => { const next = new Set(prev); visible.forEach(e => next.add(e)); return next; });
    }
  };

  const miniSelectedStats = useMemo(() => {
    const sel = miniResources.filter(r => selectedEmails.has(r.email));
    return { count: sel.length, tasks: sel.reduce((s, r) => s + r.task_count, 0), amount: sel.reduce((s, r) => s + r.total_amount, 0) };
  }, [miniResources, selectedEmails]);

  const saveMini = async () => {
    if (selectedEmails.size === 0) { toast.error("Select at least one resource"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/mini-batches`, { name: miniName || `Mini Batch ${new Date().toLocaleDateString()}`, selected_emails: Array.from(selectedEmails) });
      toast.success("Mini batch created");
      setMiniView("list"); setSelectedEmails(new Set()); setMiniName("");
      await fetchMiniBatches();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create mini batch"); }
    finally { setSaving(false); }
  };

  const fetchMiniDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/mini-batches/${id}`);
      setMiniDetail(res.data);
    } catch { toast.error("Failed to load mini batch details"); }
  };

  const lockMini = async () => {
    if (!selectedBatch) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/mini-batches/${selectedBatch}/lock`);
      toast.success(`Locked ${res.data.locked_count} mini assignments`);
      setShowLockConfirm(false);
      await fetchMiniBatches();
      await fetchMiniDetail(selectedBatch);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to lock"); }
    finally { setSaving(false); }
  };

  const deleteMini = async () => {
    if (!selectedBatch) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/mini-batches/${selectedBatch}`);
      toast.success("Mini batch deleted");
      setShowDeleteConfirm(false);
      setSelectedBatch(null); setMiniDetail(null); setMiniView("list");
      await fetchMiniBatches();
    } catch { toast.error("Failed to delete"); }
    finally { setSaving(false); }
  };

  if (loading && activeTab === "regular") {
    return (
      <div className="p-6 flex items-center justify-center h-64" data-testid="batches-loading">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
          <p className="text-slate-500 text-sm">Loading batches...</p>
        </div>
      </div>
    );
  }

  /* ============================== */
  /* TAB BAR                        */
  /* ============================== */
  const TabBar = () => (
    <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5 mb-6 w-fit" data-testid="batch-tab-bar">
      <button
        onClick={() => { setActiveTab("regular"); setView("list"); }}
        className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === "regular" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
        data-testid="tab-regular"
      >
        <Package className="w-4 h-4" /> Regular Batches
      </button>
      <button
        onClick={() => { setActiveTab("mini"); setMiniView("list"); }}
        className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === "mini" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
        data-testid="tab-mini"
      >
        <ClipboardList className="w-4 h-4" /> Mini Assignment Batches
      </button>
    </div>
  );

  /* ============================== */
  /* MINI: DETAIL VIEW              */
  /* ============================== */
  if (activeTab === "mini" && miniView === "detail" && miniDetail) {
    const isLocked = miniDetail.status === "locked";
    return (
      <div className="p-6" data-testid="mini-detail-view">
        <TabBar />
        <div className="flex items-center gap-3 mb-6">
          <Button variant="outline" size="sm" onClick={() => { setMiniView("list"); setMiniDetail(null); setSelectedBatch(null); }}>
            <ChevronLeft className="w-4 h-4 mr-1" />Back
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900">{miniDetail.name}</h1>
              <Badge className={isLocked ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                {isLocked ? <><Lock className="w-3 h-3 mr-1" />Locked</> : <><Clock className="w-3 h-3 mr-1" />Draft</>}
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isLocked && (
              <Button size="sm" onClick={() => setShowLockConfirm(true)} data-testid="lock-mini-btn">
                <Lock className="w-4 h-4 mr-1" />Lock Batch
              </Button>
            )}
            <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => setShowDeleteConfirm(true)} data-testid="delete-mini-btn">
              <Trash2 className="w-4 h-4 mr-1" />Delete
            </Button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-br from-purple-500 to-violet-600 rounded-2xl p-4 text-white">
            <p className="text-xs text-white/70 mb-1">Resources</p>
            <p className="text-xl font-bold">{miniDetail.resources?.length || 0}</p>
          </div>
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-4 text-white">
            <p className="text-xs text-white/70 mb-1">Assignments</p>
            <p className="text-xl font-bold">{miniDetail.task_count}</p>
          </div>
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-4 text-white">
            <p className="text-xs text-white/70 mb-1">Total Amount</p>
            <p className="text-xl font-bold">{fmt(miniDetail.total_amount || 0)}</p>
          </div>
        </div>

        {/* Resource table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Users className="w-4 h-4 text-purple-600" />Resources in Batch
            </CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm" data-testid="mini-detail-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase">#</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase">Resource</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase">Task Types</th>
                  <th className="text-right py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase">Assignments</th>
                  <th className="text-right py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase">Duration</th>
                  <th className="text-right py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(miniDetail.resources || []).map((r, i) => (
                  <tr key={r.email} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2.5 px-3 text-xs text-slate-400">{i + 1}</td>
                    <td className="py-2.5 px-3">
                      <span className="text-sm font-medium text-slate-800">{r.display_name || r.email}</span>
                      <p className="text-[10px] text-slate-400">{r.email}</p>
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="flex gap-1 flex-wrap">
                        {(r.task_types || []).map(t => (
                          <Badge key={t} className="text-[9px] bg-purple-50 text-purple-600 border-0">{t}</Badge>
                        ))}
                      </div>
                    </td>
                    <td className="py-2.5 px-3 text-right text-sm text-slate-700">{r.task_count}</td>
                    <td className="py-2.5 px-3 text-right text-sm text-slate-700">{Math.round(r.total_duration).toLocaleString()}</td>
                    <td className="py-2.5 px-3 text-right text-sm font-semibold text-slate-900">{fmtFull(r.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
              {(miniDetail.resources || []).length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-slate-300 bg-slate-50">
                    <td className="py-3 px-3" />
                    <td className="py-3 px-3 text-sm font-bold text-slate-800">Total</td>
                    <td className="py-3 px-3" />
                    <td className="py-3 px-3 text-right text-sm font-bold text-slate-800">{miniDetail.task_count}</td>
                    <td className="py-3 px-3 text-right text-sm font-bold text-slate-800">{Math.round(miniDetail.total_duration || 0).toLocaleString()}</td>
                    <td className="py-3 px-3 text-right text-sm font-bold text-slate-900">{fmtFull(miniDetail.total_amount || 0)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </CardContent>
        </Card>

        {/* Confirm Dialogs */}
        <Dialog open={showLockConfirm} onOpenChange={setShowLockConfirm}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Lock this mini batch?</DialogTitle>
              <DialogDescription>This will reserve {miniDetail.task_count} mini assignments for invoicing.</DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => setShowLockConfirm(false)}>Cancel</Button>
              <Button size="sm" onClick={lockMini} disabled={saving}><Lock className="w-3.5 h-3.5 mr-1" />{saving ? "Locking..." : "Lock"}</Button>
            </div>
          </DialogContent>
        </Dialog>
        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Delete this mini batch?</DialogTitle>
              <DialogDescription>{miniDetail.status === "locked" ? "This will unlock all assignments." : "This draft batch will be permanently deleted."}</DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
              <Button size="sm" variant="destructive" onClick={deleteMini} disabled={saving}><Trash2 className="w-3.5 h-3.5 mr-1" />{saving ? "Deleting..." : "Delete"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  /* ============================== */
  /* MINI: CREATE VIEW              */
  /* ============================== */
  if (activeTab === "mini" && miniView === "create") {
    return (
      <div className="p-6" data-testid="mini-create-view">
        <TabBar />
        <div className="flex items-center gap-3 mb-6">
          <Button variant="outline" size="sm" onClick={() => setMiniView("list")}><ChevronLeft className="w-4 h-4 mr-1" />Cancel</Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900">Create Mini Assignment Batch</h1>
            <p className="text-sm text-slate-500 mt-0.5">Select resources to include in this batch</p>
          </div>
        </div>

        {/* Batch name */}
        <Card className="mb-4">
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <Label className="text-sm font-medium text-slate-700 whitespace-nowrap">Batch Name</Label>
              <Input value={miniName} onChange={e => setMiniName(e.target.value)} placeholder="e.g., March Mini Batch" className="max-w-sm" data-testid="mini-name-input" />
            </div>
          </CardContent>
        </Card>

        {/* Selection summary */}
        {selectedEmails.size > 0 && (
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-4 flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-semibold text-purple-800">{miniSelectedStats.count} resources</span>
              </div>
              <span className="text-sm text-purple-600">{miniSelectedStats.tasks} assignments</span>
              <span className="text-sm font-semibold text-purple-800">{fmtFull(miniSelectedStats.amount)}</span>
            </div>
            <Button size="sm" onClick={saveMini} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid="save-mini-btn">
              <Package className="w-4 h-4 mr-1" />{saving ? "Creating..." : "Create Batch"}
            </Button>
          </div>
        )}

        {/* Resource selection */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Users className="w-4 h-4 text-purple-600" />Available Resources
                <Badge variant="secondary" className="text-[10px]">{miniResources.length} with unbatched assignments</Badge>
              </CardTitle>
              <div className="flex items-center gap-3">
                <div className="relative w-52">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <Input placeholder="Search..." value={miniSearch} onChange={e => setMiniSearch(e.target.value)} className="pl-8 h-8 text-xs" data-testid="mini-search" />
                </div>
                <Button variant="outline" size="sm" onClick={toggleAllEmails} data-testid="select-all-emails">
                  {filteredMiniResources.every(r => selectedEmails.has(r.email)) ? "Deselect All" : "Select All"}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm" data-testid="mini-resource-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-center py-2.5 px-3 w-10"></th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase">Resource</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase">Task Types</th>
                  <th className="text-right py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase">Assignments</th>
                  <th className="text-right py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase">Duration</th>
                  <th className="text-right py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase">Amount</th>
                </tr>
              </thead>
              <tbody>
                {filteredMiniResources.map((r) => {
                  const checked = selectedEmails.has(r.email);
                  return (
                    <tr key={r.email} className={`border-b border-slate-100 cursor-pointer transition-colors ${checked ? "bg-purple-50" : "hover:bg-slate-50"}`} onClick={() => toggleEmail(r.email)} data-testid={`mini-resource-row-${r.email}`}>
                      <td className="py-2.5 px-3 text-center"><Checkbox checked={checked} onCheckedChange={() => toggleEmail(r.email)} /></td>
                      <td className="py-2.5 px-3">
                        <span className="text-sm font-medium text-slate-800">{r.display_name || r.email}</span>
                        {r.display_name && r.display_name !== r.email && <p className="text-[10px] text-slate-400">{r.email}</p>}
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="flex gap-1 flex-wrap">
                          {(r.task_types || []).map(t => <Badge key={t} className="text-[9px] bg-purple-50 text-purple-600 border-0">{t}</Badge>)}
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-right text-sm text-slate-700">{r.task_count}</td>
                      <td className="py-2.5 px-3 text-right text-sm text-slate-700">{Math.round(r.total_duration).toLocaleString()}</td>
                      <td className="py-2.5 px-3 text-right text-sm font-semibold text-slate-900">{fmtFull(r.total_amount)}</td>
                    </tr>
                  );
                })}
                {filteredMiniResources.length === 0 && (
                  <tr><td colSpan={6} className="py-8 text-center text-sm text-slate-400">No resources with unbatched assignments</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    );
  }

  /* ============================== */
  /* MINI: LIST VIEW                */
  /* ============================== */
  if (activeTab === "mini") {
    return (
      <div className="p-6 max-w-full overflow-hidden" data-testid="mini-list-view">
        <div className="flex items-center justify-between mb-2 gap-4 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-slate-900">Invoice Batches</h1>
            <p className="text-sm text-slate-500 mt-0.5">Monthly project selection for invoicing</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={fetchMiniBatches}><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
            <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={() => { setMiniView("create"); setSelectedEmails(new Set()); setMiniName(""); }} data-testid="create-mini-batch-btn">
              <Plus className="w-4 h-4 mr-1" />Create Mini Batch
            </Button>
          </div>
        </div>
        <TabBar />
        {miniLoading ? (
          <div className="flex items-center justify-center py-16"><div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" /></div>
        ) : miniBatches.length > 0 ? (
          <div className="space-y-3">
            {miniBatches.map(b => {
              const isLocked = b.status === "locked";
              return (
                <Card key={b.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => { setSelectedBatch(b.id); fetchMiniDetail(b.id); setMiniView("detail"); }} data-testid={`mini-batch-card-${b.id}`}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isLocked ? "bg-emerald-100" : "bg-purple-100"}`}>
                          {isLocked ? <Lock className="w-6 h-6 text-emerald-600" /> : <ClipboardList className="w-6 h-6 text-purple-600" />}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-base font-semibold text-slate-900">{b.name}</h3>
                            <Badge className={isLocked ? "bg-emerald-100 text-emerald-700 text-[10px]" : "bg-amber-100 text-amber-700 text-[10px]"}>{isLocked ? "Locked" : "Draft"}</Badge>
                          </div>
                          <p className="text-sm text-slate-500">{b.selected_emails?.length || 0} resources &middot; {b.task_count} assignments</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-base font-bold text-slate-900">{fmt(b.total_amount || 0)}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <ClipboardList className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500 font-medium">No mini assignment batches yet</p>
            <p className="text-sm text-slate-400 mt-1">Create a batch to group mini assignments by resource for invoicing</p>
            <Button size="sm" className="mt-4 bg-purple-600 hover:bg-purple-700" onClick={() => { setMiniView("create"); setSelectedEmails(new Set()); setMiniName(""); }} data-testid="create-first-mini-btn">
              <Plus className="w-4 h-4 mr-1" />Create First Mini Batch
            </Button>
          </div>
        )}
      </div>
    );
  }

  /* ============================== */
  /* REGULAR: ARCHIVED VIEW         */
  /* ============================== */
  if (view === "archived") {
    return (
      <div className="p-6" data-testid="archived-view">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="outline" size="sm" onClick={() => setView("list")}><ChevronLeft className="w-4 h-4 mr-1" />Back</Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900">Archived Tasks</h1>
            <p className="text-sm text-slate-500 mt-0.5">Tasks excluded from invoice batches</p>
          </div>
        </div>
        {archivedGroups.length > 0 ? (
          <div className="space-y-3">
            {archivedGroups.map((g, i) => (
              <Card key={i} className="hover:shadow-sm transition-shadow">
                <CardContent className="py-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center"><Archive className="w-5 h-5 text-slate-500" /></div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{g.main_title}</p>
                      <p className="text-xs text-slate-500">{fmtMonth(g.month)} &middot; {g.task_count} tasks &middot; Dur: {Math.round(g.total_duration).toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="text-sm font-semibold text-slate-700">{fmtFull(g.total_amount)}</p>
                    <Button variant="outline" size="sm" onClick={() => unarchiveTasks(g.task_ids)} data-testid={`unarchive-btn-${i}`}><ArchiveRestore className="w-3.5 h-3.5 mr-1" />Unarchive</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 text-slate-400"><Archive className="w-10 h-10 mx-auto mb-3 text-slate-300" /><p className="text-sm">No archived tasks</p></div>
        )}
      </div>
    );
  }

  /* ============================== */
  /* REGULAR: BATCH DETAIL VIEW     */
  /* ============================== */
  if (view === "detail" && batchDetail) {
    const isLocked = batchDetail.status === "locked";
    return (
      <div className="p-6" data-testid="batch-detail-view">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="outline" size="sm" onClick={() => { setView("list"); setBatchDetail(null); setSelectedBatch(null); }}><ChevronLeft className="w-4 h-4 mr-1" />Back</Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900">{batchDetail.name}</h1>
              <Badge className={isLocked ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-amber-100 text-amber-700 hover:bg-amber-100"}>{isLocked ? <><Lock className="w-3 h-3 mr-1" />Locked</> : <><Clock className="w-3 h-3 mr-1" />Draft</>}</Badge>
            </div>
            <p className="text-sm text-slate-500 mt-0.5">Database: {fmtMonth(batchDetail.month)}{batchDetail.billing_month ? ` \u00B7 Billing: ${fmtMonth(batchDetail.billing_month)}` : ""}</p>
          </div>
          <div className="flex items-center gap-2">
            {!isLocked && <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={openAddTasks} data-testid="add-tasks-btn"><Plus className="w-4 h-4 mr-1" />Add Tasks</Button>}
            {!isLocked && <Button size="sm" onClick={() => setShowLockConfirm(true)} data-testid="lock-batch-btn"><Lock className="w-4 h-4 mr-1" />Lock Batch</Button>}
            {isLocked && <Button variant="outline" size="sm" className="text-amber-600 hover:text-amber-700 border-amber-300" onClick={unlockBatch} disabled={saving} data-testid="unlock-batch-btn"><Unlock className="w-4 h-4 mr-1" />{saving ? "Unlocking..." : "Unlock"}</Button>}
            {isLocked && <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSendNotifications} disabled={sendingEmails} data-testid="send-email-btn">{sendingEmails ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}{sendingEmails ? "Sending..." : "Send Email"}</Button>}
            {isLocked && <Button variant="outline" size="sm" onClick={() => setShowArchiveConfirm(true)} data-testid="archive-unselected-btn"><Archive className="w-4 h-4 mr-1" />Archive Unselected</Button>}
            {isLocked && <Button variant="outline" size="sm" className="text-violet-600 border-violet-200" onClick={async () => {
              if (!window.confirm("Archive this batch? It will be moved to archive.")) return;
              try { await axios.post(`${API}/batches/${selectedBatch}/archive`); toast.success("Batch archived"); fetchBatches(); } catch { toast.error("Failed"); }
            }} data-testid="archive-batch-btn"><Archive className="w-4 h-4 mr-1" />Archive Batch</Button>}
            <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => setShowDeleteConfirm(true)} data-testid="delete-batch-btn"><Trash2 className="w-4 h-4 mr-1" />Delete</Button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-br from-indigo-500 to-violet-600 rounded-2xl p-4 text-white"><p className="text-xs text-white/70 mb-1">Projects</p><p className="text-xl font-bold">{batchDetail.project_count || batchDetail.projects?.length || 0}</p></div>
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-4 text-white"><p className="text-xs text-white/70 mb-1">Tasks</p><p className="text-xl font-bold">{batchDetail.task_count}</p></div>
          <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-4 text-white"><p className="text-xs text-white/70 mb-1">Total Duration</p><p className="text-xl font-bold">{Math.round(batchDetail.total_duration || 0).toLocaleString()}</p></div>
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-4 text-white"><p className="text-xs text-white/70 mb-1">Total Amount</p><p className="text-xl font-bold">{fmt(batchDetail.total_amount || 0)}</p></div>
        </div>

        {/* Tab Bar: Tasks | Billings | History */}
        <div className="flex items-center gap-2 mb-3">
          {["tasks", "billings", "history"].map(tab => (
            <button key={tab} onClick={() => setBatchTab(tab)}
              className={`px-4 py-1.5 rounded-full text-[11px] font-semibold capitalize transition-colors ${batchTab === tab ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
              data-testid={`batch-tab-${tab}`}>
              {tab === "tasks" ? `Tasks (${detailTotal})` : tab === "billings" ? "Billings" : "History"}
            </button>
          ))}
          {/* Resource Dropdown */}
          {batchTab === "tasks" && batchDetail.resource_breakdown?.length > 0 && (
            <div className="ml-auto flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                <select value={resourceFilter} onChange={e => { setResourceFilter(e.target.value); }}
                  className="h-8 pl-8 pr-3 rounded-lg border border-slate-200 text-[11px] bg-white appearance-none min-w-[200px]" data-testid="resource-dropdown">
                  <option value="">All Resources ({batchDetail.resource_breakdown.length})</option>
                  {batchDetail.resource_breakdown.map(r => (
                    <option key={r.email} value={r.email}>{r.name} — {r.task_count} tasks — {fmtFull(r.total_amount)}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Selected resource summary */}
        {batchTab === "tasks" && resourceFilter && batchDetail.resource_breakdown && (() => {
          const r = batchDetail.resource_breakdown.find(rb => rb.email === resourceFilter);
          if (!r) return null;
          return (
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-3 mb-3 flex items-center gap-6">
              <div><p className="text-sm font-semibold text-slate-800">{r.name}</p><p className="text-[10px] text-slate-400">{r.email}</p></div>
              <div className="text-center"><p className="text-sm font-bold text-slate-700">{r.task_count}</p><p className="text-[9px] text-slate-400">Tasks</p></div>
              <div className="text-center"><p className="text-sm font-bold text-slate-700">{r.project_count}</p><p className="text-[9px] text-slate-400">Projects</p></div>
              <div className="text-center"><p className="text-sm font-bold text-slate-700">{Math.round(r.total_duration).toLocaleString()}</p><p className="text-[9px] text-slate-400">Duration</p></div>
              <div className="text-center ml-auto"><p className="text-sm font-bold text-emerald-700">{fmtFull(r.total_amount)}</p><p className="text-[9px] text-slate-400">Earnings</p></div>
            </div>
          );
        })()}

        {/* ═══ BILLINGS TAB ═══ */}
        {batchTab === "billings" && batchDetail.resource_breakdown?.length > 0 && (
          <Card data-testid="billings-card">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-slate-500 uppercase">#</th>
                      <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-slate-500 uppercase">Resource</th>
                      <th className="px-3 py-2.5 text-center text-[10px] font-semibold text-slate-500 uppercase cursor-pointer hover:text-blue-600" onClick={() => setBillSort(s => s === "tasks_desc" ? "tasks_asc" : "tasks_desc")}>Tasks</th>
                      <th className="px-3 py-2.5 text-center text-[10px] font-semibold text-slate-500 uppercase cursor-pointer hover:text-blue-600" onClick={() => setBillSort(s => s === "projects_desc" ? "projects_asc" : "projects_desc")}>Projects</th>
                      <th className="px-3 py-2.5 text-center text-[10px] font-semibold text-slate-500 uppercase cursor-pointer hover:text-blue-600" onClick={() => setBillSort(s => s === "duration_desc" ? "duration_asc" : "duration_desc")}>Duration</th>
                      <th className="px-3 py-2.5 text-right text-[10px] font-semibold text-slate-500 uppercase cursor-pointer hover:text-blue-600" onClick={() => setBillSort(s => s === "amount_desc" ? "amount_asc" : "amount_desc")}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...batchDetail.resource_breakdown].sort((a, b) => {
                      const [field, dir] = (billSort || "amount_desc").split("_");
                      const mul = dir === "asc" ? 1 : -1;
                      if (field === "tasks") return (a.task_count - b.task_count) * mul;
                      if (field === "projects") return (a.project_count - b.project_count) * mul;
                      if (field === "duration") return (a.total_duration - b.total_duration) * mul;
                      return (a.total_amount - b.total_amount) * mul;
                    }).map((r, i) => (
                      <tr key={r.email} className="border-b border-slate-100 hover:bg-slate-50/50">
                        <td className="px-3 py-2.5 text-[11px] text-slate-400">{i + 1}</td>
                        <td className="px-3 py-2.5">
                          <p className="text-[12px] font-medium text-slate-800">{r.name}</p>
                          <p className="text-[9px] text-slate-400">{r.email}</p>
                        </td>
                        <td className="px-3 py-2.5 text-center text-[12px] font-semibold text-slate-700">{r.task_count}</td>
                        <td className="px-3 py-2.5 text-center text-[11px] text-slate-600">{r.project_count}</td>
                        <td className="px-3 py-2.5 text-center text-[11px] text-slate-600">{Math.round(r.total_duration).toLocaleString()}</td>
                        <td className="px-3 py-2.5 text-right text-[12px] font-bold text-emerald-700">{fmtFull(r.total_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-100 font-semibold border-t-2 border-slate-300">
                      <td colSpan={2} className="px-3 py-3 text-[11px] text-slate-600 uppercase">Total ({batchDetail.resource_breakdown.length} resources)</td>
                      <td className="px-3 py-3 text-center text-[12px] text-slate-800">{batchDetail.task_count}</td>
                      <td className="px-3 py-3 text-center text-[11px] text-slate-700">{batchDetail.project_count}</td>
                      <td className="px-3 py-3 text-center text-[11px] text-slate-700">{Math.round(batchDetail.total_duration || 0).toLocaleString()}</td>
                      <td className="px-3 py-3 text-right text-[13px] font-bold text-slate-900">{fmtFull(batchDetail.total_amount || 0)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ═══ HISTORY TAB ═══ */}
        {batchTab === "history" && (
          <Card data-testid="batch-history-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">Batch History</CardTitle>
              <p className="text-[11px] text-slate-400">Lock, unlock, edits, and revert events for this batch</p>
            </CardHeader>
            <CardContent className="p-0">
              <EntityHistory collection="invoice_batches" entityId={batchDetail.id} compact onChanged={() => fetchBatchDetail(batchDetail.id)} />
            </CardContent>
          </Card>
        )}

        {/* ═══ TASKS TAB ═══ */}
        {batchTab === "tasks" && (
        <Card data-testid="batch-tasks-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2"><FileText className="w-4 h-4 text-indigo-600" />Tasks ({detailTotal})</CardTitle>
              <div className="flex items-center gap-2">
                {!isLocked && selectedDetailIds.size > 0 && (
                  <Button variant="outline" size="sm" className="h-7 text-[10px] text-red-600 border-red-200" onClick={removeBulkTasks} disabled={removingBulk} data-testid="bulk-remove-btn">
                    {removingBulk ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
                    Remove {selectedDetailIds.size} selected
                  </Button>
                )}
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => fetchDetailTasks(selectedBatch, detailPage)}><RefreshCw className="w-3.5 h-3.5 text-slate-400" /></Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {/* Filters */}
            <div className="flex items-center gap-2 flex-wrap px-4 pb-3">
              <div className="relative flex-1 min-w-[150px]">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                <Input value={detailSearch} onChange={e => setDetailSearch(e.target.value)} placeholder="Search project, episode, resource..." className="pl-7 h-7 text-[11px]" data-testid="detail-tasks-search" />
              </div>
              <Select value={detailTaskType || "all"} onValueChange={v => setDetailTaskType(v === "all" ? "" : v)}>
                <SelectTrigger className="h-7 text-[10px] w-28" data-testid="detail-task-type-filter"><SelectValue placeholder="All Tasks" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tasks</SelectItem>
                  {detailFilterOpts.task_types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={detailSortBy} onValueChange={setDetailSortBy}>
                <SelectTrigger className="h-7 text-[10px] w-32" data-testid="detail-sort-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="due_date">Sort: Due Date</SelectItem>
                  <SelectItem value="amount">Sort: Amount</SelectItem>
                  <SelectItem value="main_title">Sort: Project</SelectItem>
                  <SelectItem value="duration_words">Sort: Duration</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setDetailSortOrder(o => o === "asc" ? "desc" : "asc")}>
                {detailSortOrder === "asc" ? <ChUp className="w-3.5 h-3.5" /> : <ChDown className="w-3.5 h-3.5" />}
              </Button>
              {(detailSearch || detailTaskType) && (
                <Button variant="ghost" size="sm" className="h-7 text-[10px] text-slate-500 px-1.5" onClick={() => { setDetailSearch(""); setDetailTaskType(""); }}>
                  <X className="w-3 h-3 mr-0.5" /> Clear
                </Button>
              )}
            </div>

            {/* Task table */}
            {detailLoading ? (
              <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-3 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" /></div>
            ) : detailTasks.length === 0 ? (
              <div className="text-center py-12 text-slate-400 text-xs">No tasks found</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="batch-tasks-table">
                    <thead className="bg-slate-50 border-y border-slate-200">
                      <tr>
                        {!isLocked && <th className="px-2 py-2 w-8"><input type="checkbox" checked={selectedDetailIds.size === detailTasks.length && detailTasks.length > 0} onChange={selectAllDetail} className="rounded w-3 h-3" /></th>}
                        <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-500">#</th>
                        <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase cursor-pointer" onClick={() => toggleDetailSort("main_title")}>Project</th>
                        <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase">Episode</th>
                        <th className="px-2 py-2 text-center text-[10px] font-semibold text-slate-500 uppercase cursor-pointer" onClick={() => toggleDetailSort("task")}>Task</th>
                        <th className="px-2 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase">Resource</th>
                        <th className="px-2 py-2 text-center text-[10px] font-semibold text-slate-500 uppercase">Lang</th>
                        <th className="px-2 py-2 text-center text-[10px] font-semibold text-slate-500 uppercase cursor-pointer" onClick={() => toggleDetailSort("due_date")}>Due Date</th>
                        <th className="px-2 py-2 text-center text-[10px] font-semibold text-slate-500 uppercase cursor-pointer" onClick={() => toggleDetailSort("duration_words")}>Dur</th>
                        <th className="px-2 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase cursor-pointer" onClick={() => toggleDetailSort("amount")}>Amount</th>
                        <th className="px-2 py-2 text-center text-[10px] font-semibold text-slate-500"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailTasks.map((t, idx) => (
                        <tr key={t.id || idx} className={`border-b border-slate-100 hover:bg-slate-50/50 transition-colors ${selectedDetailIds.has(t.id) ? "bg-blue-50/50" : ""}`} data-testid={`detail-task-row-${idx}`}>
                          {!isLocked && <td className="px-2 py-1.5"><input type="checkbox" checked={selectedDetailIds.has(t.id)} onChange={() => toggleDetailSelect(t.id)} className="rounded w-3 h-3" /></td>}
                          <td className="px-2 py-1.5 text-[10px] text-slate-400">{(detailPage - 1) * 50 + idx + 1}</td>
                          <td className="px-2 py-1.5 text-[11px] font-medium text-slate-700 max-w-[160px] truncate" title={t.main_title}>{t.main_title || "-"}</td>
                          <td className="px-2 py-1.5 text-[11px] text-slate-600 max-w-[180px] truncate" title={`${t.episode_title || ""} ${t.season_number ? `· S${t.season_number}` : ""}${t.episode_number ? ` · E${t.episode_number}` : ""}`}>
                            {(t.season_number || t.episode_number) && (
                              <span className="inline-block mr-1 px-1 rounded bg-violet-50 text-violet-700 text-[9px] font-medium align-middle">
                                {t.season_number ? `S${t.season_number}` : ""}{t.season_number && t.episode_number ? "·" : ""}{t.episode_number ? `E${t.episode_number}` : ""}
                              </span>
                            )}
                            <span className="align-middle">{t.episode_title || "-"}</span>
                          </td>
                          <td className="px-2 py-1.5 text-center"><Badge className="bg-blue-50 text-blue-700 border-0 text-[9px]">{t.task || "-"}</Badge></td>
                          <td className="px-2 py-1.5 text-[11px] text-slate-700 max-w-[140px] truncate">{t.resource || "-"}</td>
                          <td className="px-2 py-1.5 text-center text-[10px] text-slate-600">{t.language || "-"}</td>
                          <td className="px-2 py-1.5 text-center text-[10px] text-slate-500 whitespace-nowrap">{t.due_date || "-"}</td>
                          <td className="px-2 py-1.5 text-center text-[11px] text-slate-600">{t.duration_words || 0}</td>
                          <td className="px-2 py-1.5 text-right text-[11px] font-semibold text-slate-700">{fmtFull(t.amount || 0)}</td>
                          <td className="px-2 py-1.5 text-center">
                              {!isLocked && <button onClick={() => removeTaskFromBatch(t.id)} className="p-1 rounded hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors" title="Remove from batch" data-testid={`remove-task-${idx}`}>
                                <Trash2 className="w-3 h-3" />
                              </button>}
                            </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between px-4 py-2 border-t border-slate-200">
                  <p className="text-[10px] text-slate-500">Showing {(detailPage - 1) * 50 + 1}\u2013{Math.min(detailPage * 50, detailTotal)} of {detailTotal}</p>
                  <div className="flex items-center gap-1.5">
                    <Button variant="outline" size="sm" className="h-6 px-2" disabled={detailPage <= 1} onClick={() => fetchDetailTasks(selectedBatch, detailPage - 1)} data-testid="detail-prev-page"><ChevronLeft className="w-3 h-3" /></Button>
                    <span className="text-[10px] text-slate-600">{detailPage}/{detailPages}</span>
                    <Button variant="outline" size="sm" className="h-6 px-2" disabled={detailPage >= detailPages} onClick={() => fetchDetailTasks(selectedBatch, detailPage + 1)} data-testid="detail-next-page"><ChevronRight className="w-3 h-3" /></Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
        )}

        {/* Preview as Translator */}
        {isLocked && batchResources.length > 0 && (
          <Card className="mt-4">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Eye className="w-4 h-4 text-indigo-600" />Preview as Translator
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <Select value={previewEmail} onValueChange={setPreviewEmail}>
                  <SelectTrigger className="w-80" data-testid="preview-email-select">
                    <SelectValue placeholder="Select a translator..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {batchResources.map(email => (
                      <SelectItem key={email} value={email}>{email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  onClick={() => previewTranslator(previewEmail)}
                  disabled={!previewEmail || previewLoading}
                  data-testid="preview-translator-btn"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  {previewLoading ? "Loading..." : "Preview"}
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-2">See what a specific translator will see in their dashboard for this batch</p>
            </CardContent>
          </Card>
        )}

        {/* Preview Dialog */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="preview-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-indigo-600" />
                Translator Preview: {previewEmail}
              </DialogTitle>
              <DialogDescription>
                This is what {previewEmail} will see in their dashboard for this batch
              </DialogDescription>
            </DialogHeader>
            {previewData ? (
              <div className="space-y-4 mt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-indigo-50 rounded-xl p-3">
                    <p className="text-xs text-indigo-600 mb-0.5">Total Tasks</p>
                    <p className="text-lg font-bold text-indigo-800">{previewData.total_tasks}</p>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-3">
                    <p className="text-xs text-emerald-600 mb-0.5">Total Amount</p>
                    <p className="text-lg font-bold text-emerald-800">{fmtFull(previewData.total_amount || 0)}</p>
                  </div>
                </div>
                {(previewData.summary || []).length > 0 && (
                  <table className="w-full text-sm" data-testid="preview-summary-table">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Task Type</th>
                        <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Count</th>
                        <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Duration</th>
                        <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(previewData.summary || []).map((s, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="py-2 px-3 text-sm text-slate-800">{s.task_type}</td>
                          <td className="py-2 px-3 text-right text-sm text-slate-700">{s.count}</td>
                          <td className="py-2 px-3 text-right text-sm text-slate-700">{Math.round(s.total_duration || 0).toLocaleString()}</td>
                          <td className="py-2 px-3 text-right text-sm font-semibold text-slate-900">{fmtFull(s.total_amount || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-slate-300 bg-slate-50">
                        <td className="py-2.5 px-3 text-sm font-bold text-slate-800">Total</td>
                        <td className="py-2.5 px-3 text-right text-sm font-bold text-slate-800">{previewData.total_tasks}</td>
                        <td className="py-2.5 px-3" />
                        <td className="py-2.5 px-3 text-right text-sm font-bold text-slate-900">{fmtFull(previewData.total_amount || 0)}</td>
                      </tr>
                    </tfoot>
                  </table>
                )}
                {previewData.total_tasks === 0 && (
                  <div className="text-center py-6 text-sm text-slate-400">No tasks found for this translator in this batch</div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={showLockConfirm} onOpenChange={setShowLockConfirm}>
          <DialogContent className="max-w-sm" data-testid="lock-confirm-dialog"><DialogHeader><DialogTitle>Lock this batch?</DialogTitle><DialogDescription>Once locked, these {batchDetail.task_count} tasks will be reserved for invoicing.</DialogDescription></DialogHeader><div className="flex justify-end gap-2 mt-4"><Button variant="outline" size="sm" onClick={() => setShowLockConfirm(false)}>Cancel</Button><Button size="sm" onClick={lockBatch} disabled={saving} data-testid="confirm-lock-btn"><Lock className="w-3.5 h-3.5 mr-1" />{saving ? "Locking..." : "Lock Batch"}</Button></div></DialogContent>
        </Dialog>
        <Dialog open={showArchiveConfirm} onOpenChange={setShowArchiveConfirm}>
          <DialogContent className="max-w-sm" data-testid="archive-confirm-dialog"><DialogHeader><DialogTitle>Archive unselected tasks?</DialogTitle><DialogDescription>Tasks from {fmtMonth(batchDetail.month)} not included in this batch will be archived.</DialogDescription></DialogHeader><div className="flex justify-end gap-2 mt-4"><Button variant="outline" size="sm" onClick={() => setShowArchiveConfirm(false)}>Cancel</Button><Button size="sm" onClick={archiveUnselected} disabled={saving} data-testid="confirm-archive-btn"><Archive className="w-3.5 h-3.5 mr-1" />{saving ? "Archiving..." : "Archive"}</Button></div></DialogContent>
        </Dialog>
        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <DialogContent className="max-w-sm" data-testid="delete-confirm-dialog"><DialogHeader><DialogTitle>Delete this batch?</DialogTitle><DialogDescription>{batchDetail.status === "locked" ? "This will unlock all tasks in this batch." : "This draft batch will be permanently deleted."}</DialogDescription></DialogHeader><div className="flex justify-end gap-2 mt-4"><Button variant="outline" size="sm" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button><Button size="sm" variant="destructive" onClick={deleteBatch} disabled={saving} data-testid="confirm-delete-btn"><Trash2 className="w-3.5 h-3.5 mr-1" />{saving ? "Deleting..." : "Delete"}</Button></div></DialogContent>
        </Dialog>

        {/* Add Tasks Dialog */}
        <Dialog open={showAddTasks} onOpenChange={setShowAddTasks}>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="add-tasks-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5 text-emerald-600" /> Add Tasks to Batch</DialogTitle>
              <DialogDescription>Select unbatched tasks to add to "{batchDetail?.name}"</DialogDescription>
            </DialogHeader>
            {addSelectedIds.size > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center justify-between">
                <span className="text-sm font-semibold text-emerald-800">{addSelectedIds.size} tasks selected</span>
                <Button size="sm" onClick={handleAddTasks} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-add-tasks-btn">
                  <Plus className="w-4 h-4 mr-1" />{saving ? "Adding..." : "Add to Batch"}
                </Button>
              </div>
            )}
            <div className="flex items-center gap-3 mb-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input placeholder="Search title or episode..." value={addSearch} onChange={e => setAddSearch(e.target.value)} className="pl-8 h-8 text-xs" data-testid="add-tasks-search" />
              </div>
              <Select value={addSortBy} onValueChange={setAddSortBy}><SelectTrigger className="h-8 text-xs w-36" data-testid="add-sort-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="amount">Sort: Amount</SelectItem><SelectItem value="due_date">Sort: Due Date</SelectItem><SelectItem value="name">Sort: Name</SelectItem><SelectItem value="tasks">Sort: Tasks</SelectItem></SelectContent></Select>
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => {
                const allIds = addTree.flatMap(t => t.task_ids);
                const allSel = allIds.every(id => addSelectedIds.has(id));
                setAddSelectedIds(prev => { const next = new Set(prev); if (allSel) allIds.forEach(id => next.delete(id)); else allIds.forEach(id => next.add(id)); return next; });
              }} data-testid="add-select-all-btn">
                {addTree.length > 0 && addTree.flatMap(t => t.task_ids).every(id => addSelectedIds.has(id)) ? "Deselect All" : "Select All"}
              </Button>
            </div>
            {addTreeLoading ? (
              <div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" /></div>
            ) : filteredAddTree.length === 0 ? (
              <div className="text-center py-8 text-sm text-slate-400">No unbatched tasks available</div>
            ) : (
              <div className="space-y-1.5 max-h-[450px] overflow-y-auto">
                {filteredAddTree.map((title) => {
                  const titleExpanded = addExpandedTitles.has(title.main_title);
                  const titleAllSelected = title.task_ids.length > 0 && title.task_ids.every(id => addSelectedIds.has(id));
                  const titleSomeSelected = title.task_ids.some(id => addSelectedIds.has(id));
                  const titleSelectedCount = title.task_ids.filter(id => addSelectedIds.has(id)).length;
                  const addFmtDue = (d) => { if (!d) return ""; const parts = d.split("-"); return parts[2] + "/" + parts[1] + "/" + parts[0].slice(2); };
                  const addDueDateLabel = (earliest, latest) => { if (!earliest) return null; if (earliest === latest) return addFmtDue(earliest); return addFmtDue(earliest) + " \u2014 " + addFmtDue(latest); };
                  return (
                    <div key={title.main_title} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`add-tree-${title.main_title}`}>
                      <div
                        className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${titleSomeSelected ? "bg-emerald-50/60" : "hover:bg-slate-50"}`}
                        onClick={() => addToggleTitle(title.main_title)}
                      >
                        <Checkbox
                          checked={titleAllSelected}
                          onCheckedChange={() => addToggleIds(title.task_ids)}
                          onClick={e => e.stopPropagation()}
                        />
                        {titleExpanded ? <ChDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                        <span className="text-sm font-semibold text-slate-800 truncate max-w-[200px]">{title.main_title}</span>
                        {titleSomeSelected && !titleAllSelected && <Badge className="text-[9px] bg-emerald-100 text-emerald-600 border-0">{titleSelectedCount}/{title.task_count}</Badge>}
                        <div className="flex items-center gap-1 flex-wrap">
                          {Object.entries(title.type_counts || {}).map(([t, c]) => (
                            <Badge key={t} className="text-[8px] bg-blue-50 text-blue-600 border-0 font-normal">{t}: {c}</Badge>
                          ))}
                        </div>
                        {title.earliest_due && <span className="text-[10px] text-slate-400 whitespace-nowrap flex items-center gap-1"><CalendarIcon className="w-3 h-3" />{addDueDateLabel(title.earliest_due, title.latest_due)}</span>}
                        <span className="text-xs text-slate-500 whitespace-nowrap">{title.task_count} tasks</span>
                        <span className="text-xs font-medium text-slate-700 whitespace-nowrap">{fmtFull(title.total_amount)}</span>
                      </div>
                      {titleExpanded && title.seasons.map((season) => {
                        const seasonKey = `add:::${title.main_title}:::${season.season_number}`;
                        const seasonExpanded = addExpandedSeasons.has(seasonKey);
                        const seasonAllSelected = season.task_ids.length > 0 && season.task_ids.every(id => addSelectedIds.has(id));
                        const seasonSomeSelected = season.task_ids.some(id => addSelectedIds.has(id));
                        return (
                          <div key={seasonKey}>
                            <div
                              className={`flex items-center gap-3 px-4 py-2.5 pl-10 cursor-pointer border-t border-slate-100 transition-colors ${seasonSomeSelected ? "bg-emerald-50/30" : "hover:bg-slate-50"}`}
                              onClick={() => addToggleSeason(seasonKey)}
                            >
                              <Checkbox
                                checked={seasonAllSelected}
                                onCheckedChange={() => addToggleIds(season.task_ids)}
                                onClick={e => e.stopPropagation()}
                              />
                              {seasonExpanded ? <ChDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                              <Badge className="text-[9px] bg-violet-100 text-violet-700 border-0">S{season.season_number || "\u2014"}</Badge>
                              <span className="text-sm text-slate-700 flex-1">Season {season.season_number || "\u2014"}</span>
                              <div className="flex items-center gap-1 flex-wrap">
                                {Object.entries(season.type_counts || {}).map(([t, c]) => (
                                  <Badge key={t} className="text-[8px] bg-blue-50 text-blue-600 border-0 font-normal">{t}: {c}</Badge>
                                ))}
                              </div>
                              {season.earliest_due && <span className="text-[10px] text-slate-400 whitespace-nowrap flex items-center gap-1"><CalendarIcon className="w-3 h-3" />{addDueDateLabel(season.earliest_due, season.latest_due)}</span>}
                              <span className="text-xs text-slate-500">{season.task_count} tasks</span>
                              <span className="text-xs font-medium text-slate-700">{fmtFull(season.total_amount)}</span>
                            </div>
                            {seasonExpanded && season.episodes.map((ep, ei) => {
                              const epAllSelected = ep.task_ids.length > 0 && ep.task_ids.every(id => addSelectedIds.has(id));
                              const epSomeSelected = ep.task_ids.some(id => addSelectedIds.has(id));
                              return (
                                <div
                                  key={ei}
                                  className={`flex items-center gap-3 px-4 py-2 pl-16 border-t border-slate-50 cursor-pointer transition-colors ${epSomeSelected ? "bg-emerald-50/20" : "hover:bg-slate-50"}`}
                                  onClick={() => addToggleIds(ep.task_ids)}
                                  data-testid={`add-tree-episode-${ei}`}
                                >
                                  <Checkbox
                                    checked={epAllSelected}
                                    onCheckedChange={() => addToggleIds(ep.task_ids)}
                                    onClick={e => e.stopPropagation()}
                                  />
                                  <span className="text-sm text-slate-600 flex-1 truncate">
                                    {ep.episode_title}
                                    {ep.episode_number && <span className="text-xs text-slate-400 ml-1.5">#{ep.episode_number}</span>}
                                  </span>
                                  {ep.earliest_due && <span className="text-[10px] text-slate-400 whitespace-nowrap flex items-center gap-1"><CalendarIcon className="w-3 h-3" />{addDueDateLabel(ep.earliest_due, ep.latest_due)}</span>}
                                  <div className="flex items-center gap-1 flex-wrap">
                                    {ep.tasks.slice(0, 3).map((tk, ti) => (
                                      <Badge key={ti} className="text-[8px] bg-slate-100 text-slate-500 border-0 font-normal">{tk.task}</Badge>
                                    ))}
                                    {ep.tasks.length > 3 && <span className="text-[9px] text-slate-400">+{ep.tasks.length - 3}</span>}
                                  </div>
                                  <span className="text-xs text-slate-400 whitespace-nowrap">{ep.task_count} tasks</span>
                                  <span className="text-xs text-slate-600 whitespace-nowrap">{fmtFull(ep.total_amount)}</span>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
                {filteredAddTree.length === 0 && !addTreeLoading && (
                  <div className="text-center py-8 text-sm text-slate-400">No projects found</div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  /* ============================== */
  /* REGULAR: CREATE VIEW           */
  /* ============================== */
  if (view === "create") {
    const hasTreeData = episodeTree.length > 0;
    const fmtDue = (d) => { if (!d) return ""; const parts = d.split("-"); return parts[2] + "/" + parts[1] + "/" + parts[0].slice(2); };
    const dueDateLabel = (earliest, latest) => { if (!earliest) return null; if (earliest === latest) return fmtDue(earliest); return fmtDue(earliest) + " \u2014 " + fmtDue(latest); };
    return (
      <div className="p-6" data-testid="create-batch-view">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="outline" size="sm" onClick={() => setView("list")}><ChevronLeft className="w-4 h-4 mr-1" />Cancel</Button>
          <div className="flex-1"><h1 className="text-2xl font-bold text-slate-900">Create Invoice Batch</h1><p className="text-sm text-slate-500 mt-0.5">Select month and projects to include</p></div>
        </div>
        <Card className="mb-6">
          <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Calendar className="w-4 h-4 text-indigo-600" /> Select Tasks By</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5 mb-4 w-fit">
              {[{key:"single",label:"Single Month",icon:Calendar},{key:"range",label:"Month Range",icon:CalendarRange},{key:"dates",label:"Due Date Range",icon:CalendarIcon}].map(m=>(
                <button key={m.key} onClick={()=>{setSelectionMode(m.key);setEpisodeTree([]);setSelectedTaskIds(new Set());}} className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all ${selectionMode===m.key?"bg-white text-indigo-700 shadow-sm":"text-slate-500 hover:text-slate-700"}`} data-testid={`mode-${m.key}`}><m.icon className="w-3.5 h-3.5" /> {m.label}</button>
              ))}
            </div>
            {selectionMode==="single" && (
              <div className="flex items-center gap-4 flex-wrap">
                <Select value={createMonth} onValueChange={onMonthSelect}><SelectTrigger className="w-72" data-testid="month-select"><SelectValue placeholder="Choose database month..." /></SelectTrigger><SelectContent>{[...availableMonths].sort((a,b)=>b.month.localeCompare(a.month)).map(m=>(<SelectItem key={m.month} value={m.month}>{fmtMonth(m.month)} ({m.task_count} tasks available)</SelectItem>))}</SelectContent></Select>
                {createMonth && <Input value={createName} onChange={e=>setCreateName(e.target.value)} placeholder="Batch name..." className="w-56" data-testid="batch-name-input" />}
                {createMonth && <div className="flex items-center gap-2"><span className="text-xs text-slate-500 font-medium whitespace-nowrap">Billing Month:</span><Select value={billingMonth} onValueChange={setBillingMonth}><SelectTrigger className="w-52" data-testid="billing-month-select"><SelectValue placeholder="Financial year month..." /></SelectTrigger><SelectContent className="max-h-64">{billingMonthOptions.map(m=>(<SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>))}</SelectContent></Select></div>}
              </div>
            )}
            {selectionMode==="range" && (
              <div className="flex items-center gap-3 flex-wrap">
                <Select value={rangeFromMonth} onValueChange={setRangeFromMonth}><SelectTrigger className="w-56" data-testid="range-from-month"><SelectValue placeholder="From month..." /></SelectTrigger><SelectContent className="max-h-64">{[...availableMonths].sort((a,b)=>b.month.localeCompare(a.month)).map(m=>(<SelectItem key={m.month} value={m.month}>{fmtMonth(m.month)} ({m.task_count})</SelectItem>))}</SelectContent></Select>
                <ArrowRight className="w-4 h-4 text-slate-400" />
                <Select value={rangeToMonth} onValueChange={setRangeToMonth}><SelectTrigger className="w-56" data-testid="range-to-month"><SelectValue placeholder="To month..." /></SelectTrigger><SelectContent className="max-h-64">{[...availableMonths].sort((a,b)=>b.month.localeCompare(a.month)).map(m=>(<SelectItem key={m.month} value={m.month}>{fmtMonth(m.month)} ({m.task_count})</SelectItem>))}</SelectContent></Select>
                <Button size="sm" onClick={onMonthRangeApply} disabled={!rangeFromMonth||!rangeToMonth} data-testid="apply-range-btn">Load Tasks</Button>
                {hasTreeData && <Input value={createName} onChange={e=>setCreateName(e.target.value)} placeholder="Batch name..." className="w-56" data-testid="batch-name-input" />}
                {hasTreeData && <div className="flex items-center gap-2"><span className="text-xs text-slate-500 font-medium whitespace-nowrap">Billing:</span><Select value={billingMonth} onValueChange={setBillingMonth}><SelectTrigger className="w-44" data-testid="billing-month-select"><SelectValue placeholder="Billing month..." /></SelectTrigger><SelectContent className="max-h-64">{billingMonthOptions.map(m=>(<SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>))}</SelectContent></Select></div>}
              </div>
            )}
            {selectionMode==="dates" && (
              <div className="flex items-center gap-3 flex-wrap">
                <Popover><PopoverTrigger asChild><Button variant="outline" size="sm" className={`h-9 w-40 justify-start font-normal ${dateFrom?"text-slate-700":"text-slate-400"}`} data-testid="date-from-picker"><CalendarIcon className="w-3.5 h-3.5 mr-1.5 text-slate-400" />{dateFrom?format(new Date(dateFrom+"T00:00:00"),"dd MMM yyyy"):"From date"}</Button></PopoverTrigger><PopoverContent className="w-auto p-0" align="start"><CalendarWidget mode="single" selected={dateFrom?new Date(dateFrom+"T00:00:00"):undefined} onSelect={d=>setDateFrom(d?format(d,"yyyy-MM-dd"):"")} initialFocus /></PopoverContent></Popover>
                <ArrowRight className="w-4 h-4 text-slate-400" />
                <Popover><PopoverTrigger asChild><Button variant="outline" size="sm" className={`h-9 w-40 justify-start font-normal ${dateTo?"text-slate-700":"text-slate-400"}`} data-testid="date-to-picker"><CalendarIcon className="w-3.5 h-3.5 mr-1.5 text-slate-400" />{dateTo?format(new Date(dateTo+"T00:00:00"),"dd MMM yyyy"):"To date"}</Button></PopoverTrigger><PopoverContent className="w-auto p-0" align="start"><CalendarWidget mode="single" selected={dateTo?new Date(dateTo+"T00:00:00"):undefined} onSelect={d=>setDateTo(d?format(d,"yyyy-MM-dd"):"")} initialFocus /></PopoverContent></Popover>
                <Button size="sm" onClick={onDateRangeApply} disabled={!dateFrom||!dateTo} data-testid="apply-dates-btn">Load Tasks</Button>
                {hasTreeData && <Input value={createName} onChange={e=>setCreateName(e.target.value)} placeholder="Batch name..." className="w-56" data-testid="batch-name-input" />}
                {hasTreeData && <div className="flex items-center gap-2"><span className="text-xs text-slate-500 font-medium whitespace-nowrap">Billing:</span><Select value={billingMonth} onValueChange={setBillingMonth}><SelectTrigger className="w-44" data-testid="billing-month-select"><SelectValue placeholder="Billing month..." /></SelectTrigger><SelectContent className="max-h-64">{billingMonthOptions.map(m=>(<SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>))}</SelectContent></Select></div>}
              </div>
            )}
          </CardContent>
        </Card>
        {hasTreeData && (<>
          {selectedTaskIds.size > 0 && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-4 flex items-center justify-between" data-testid="selection-summary">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-indigo-600" /><span className="text-sm font-semibold text-indigo-800">{selectedStats.tasks} tasks selected</span></div>
                <span className="text-sm text-indigo-600">Duration: {Math.round(selectedStats.duration).toLocaleString()}</span>
                <span className="text-sm font-semibold text-indigo-800">{fmtFull(selectedStats.amount)}</span>
              </div>
              <Button size="sm" onClick={saveBatch} disabled={saving} data-testid="save-batch-btn"><Package className="w-4 h-4 mr-1" />{saving ? "Creating..." : "Create Batch"}</Button>
            </div>
          )}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <FileText className="w-4 h-4 text-indigo-600" />Episode Tree
                  <Badge variant="secondary" className="text-[10px]">{episodeTree.length} titles</Badge>
                </CardTitle>
                <div className="flex items-center gap-3">
                  <Select value={treeSortBy} onValueChange={setTreeSortBy}><SelectTrigger className="h-8 text-xs w-36" data-testid="tree-sort-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="amount">Sort: Amount</SelectItem><SelectItem value="due_date">Sort: Due Date</SelectItem><SelectItem value="name">Sort: Name</SelectItem><SelectItem value="tasks">Sort: Tasks</SelectItem></SelectContent></Select>
                  <div className="relative w-52">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <Input placeholder="Search title or episode..." value={treeSearch} onChange={e => setTreeSearch(e.target.value)} className="pl-8 h-8 text-xs" data-testid="tree-search" />
                  </div>
                  <Button variant="outline" size="sm" onClick={() => {
                    const allIds = episodeTree.flatMap(t => t.task_ids);
                    const allSelected = allIds.every(id => selectedTaskIds.has(id));
                    toggleTaskIds(allIds, !allSelected);
                  }} data-testid="select-all-tree-btn">
                    {episodeTree.length > 0 && episodeTree.flatMap(t => t.task_ids).every(id => selectedTaskIds.has(id)) ? "Deselect All" : "Select All"}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingTree ? (
                <div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" /></div>
              ) : (
                <div className="space-y-1.5">
                  {filteredTree.map((title) => {
                    const titleExpanded = expandedTitles.has(title.main_title);
                    const titleAllSelected = title.task_ids.length > 0 && title.task_ids.every(id => selectedTaskIds.has(id));
                    const titleSomeSelected = title.task_ids.some(id => selectedTaskIds.has(id));
                    const titleSelectedCount = title.task_ids.filter(id => selectedTaskIds.has(id)).length;
                    return (
                      <div key={title.main_title} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`tree-title-${title.main_title}`}>
                        <div
                          className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${titleSomeSelected ? "bg-indigo-50/60" : "hover:bg-slate-50"}`}
                          onClick={() => toggleTitle(title.main_title)}
                        >
                          <Checkbox
                            checked={titleAllSelected}
                            onCheckedChange={() => toggleTaskIds(title.task_ids)}
                            onClick={e => e.stopPropagation()}
                          />
                          {titleExpanded ? <ChDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                          <span className="text-sm font-semibold text-slate-800 truncate max-w-[200px]">{title.main_title}</span>
                          {titleSomeSelected && !titleAllSelected && <Badge className="text-[9px] bg-indigo-100 text-indigo-600 border-0">{titleSelectedCount}/{title.task_count}</Badge>}
                          <div className="flex items-center gap-1 flex-wrap">
                            {Object.entries(title.type_counts || {}).map(([t, c]) => (
                              <Badge key={t} className="text-[8px] bg-blue-50 text-blue-600 border-0 font-normal">{t}: {c}</Badge>
                            ))}
                          </div>
                          {title.already_batched > 0 && (
                            <span className="flex items-center gap-1 flex-shrink-0">
                              <Badge className="text-[8px] bg-amber-50 text-amber-600 border border-amber-200 font-normal">
                                <Lock className="w-2.5 h-2.5 mr-0.5" />
                                {Object.entries(title.batched_types || {}).map(([t, c]) => `${t}:${c}`).join(", ") || title.already_batched}
                              </Badge>
                            </span>
                          )}
                          {title.earliest_due && <span className="text-[10px] text-slate-400 whitespace-nowrap flex items-center gap-1"><CalendarIcon className="w-3 h-3" />{dueDateLabel(title.earliest_due, title.latest_due)}</span>}
                          <span className="text-xs text-slate-500 whitespace-nowrap">{title.task_count} tasks</span>
                          <span className="text-xs font-medium text-slate-700 whitespace-nowrap">{fmtFull(title.total_amount)}</span>
                        </div>
                        {titleExpanded && title.seasons.map((season) => {
                          const seasonKey = `${title.main_title}:::${season.season_number}`;
                          const seasonExpanded = expandedSeasons.has(seasonKey);
                          const seasonAllSelected = season.task_ids.length > 0 && season.task_ids.every(id => selectedTaskIds.has(id));
                          const seasonSomeSelected = season.task_ids.some(id => selectedTaskIds.has(id));
                          return (
                            <div key={seasonKey}>
                              <div
                                className={`flex items-center gap-3 px-4 py-2.5 pl-10 cursor-pointer border-t border-slate-100 transition-colors ${seasonSomeSelected ? "bg-indigo-50/30" : "hover:bg-slate-50"}`}
                                onClick={() => toggleSeason(seasonKey)}
                              >
                                <Checkbox
                                  checked={seasonAllSelected}
                                  onCheckedChange={() => toggleTaskIds(season.task_ids)}
                                  onClick={e => e.stopPropagation()}
                                />
                                {seasonExpanded ? <ChDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                                <Badge className="text-[9px] bg-violet-100 text-violet-700 border-0">S{season.season_number || "\u2014"}</Badge>
                                <span className="text-sm text-slate-700 flex-1">Season {season.season_number || "\u2014"}</span>
                                <div className="flex items-center gap-1 flex-wrap">
                                  {Object.entries(season.type_counts || {}).map(([t, c]) => (
                                    <Badge key={t} className="text-[8px] bg-blue-50 text-blue-600 border-0 font-normal">{t}: {c}</Badge>
                                  ))}
                                </div>
                                {season.already_batched > 0 && (
                                  <Badge className="text-[8px] bg-amber-50 text-amber-600 border border-amber-200 font-normal flex-shrink-0">
                                    <Lock className="w-2.5 h-2.5 mr-0.5" />
                                    {Object.entries(season.batched_types || {}).map(([t, c]) => `${t}:${c}`).join(", ")}
                                  </Badge>
                                )}
                                {season.earliest_due && <span className="text-[10px] text-slate-400 whitespace-nowrap flex items-center gap-1"><CalendarIcon className="w-3 h-3" />{dueDateLabel(season.earliest_due, season.latest_due)}</span>}
                                <span className="text-xs text-slate-500">{season.task_count} tasks</span>
                                <span className="text-xs font-medium text-slate-700">{fmtFull(season.total_amount)}</span>
                              </div>
                              {seasonExpanded && season.episodes.map((ep, ei) => {
                                const epAllSelected = ep.task_ids.length > 0 && ep.task_ids.every(id => selectedTaskIds.has(id));
                                const epSomeSelected = ep.task_ids.some(id => selectedTaskIds.has(id));
                                return (
                                  <div
                                    key={ei}
                                    className={`flex items-center gap-3 px-4 py-2 pl-16 border-t border-slate-50 cursor-pointer transition-colors ${epSomeSelected ? "bg-indigo-50/20" : "hover:bg-slate-50"}`}
                                    onClick={() => toggleTaskIds(ep.task_ids)}
                                    data-testid={`tree-episode-${ei}`}
                                  >
                                    <Checkbox
                                      checked={epAllSelected}
                                      onCheckedChange={() => toggleTaskIds(ep.task_ids)}
                                      onClick={e => e.stopPropagation()}
                                    />
                                    <span className="text-sm text-slate-600 flex-1 truncate">
                                      {ep.episode_title}
                                      {ep.episode_number && <span className="text-xs text-slate-400 ml-1.5">#{ep.episode_number}</span>}
                                    </span>
                                    {ep.earliest_due && <span className="text-[10px] text-slate-400 whitespace-nowrap flex items-center gap-1"><CalendarIcon className="w-3 h-3" />{dueDateLabel(ep.earliest_due, ep.latest_due)}</span>}
                                    <div className="flex items-center gap-1 flex-wrap">
                                      {ep.tasks.slice(0, 3).map((tk, ti) => (
                                        <Badge key={ti} className="text-[8px] bg-slate-100 text-slate-500 border-0 font-normal">{tk.task}</Badge>
                                      ))}
                                      {ep.tasks.length > 3 && <span className="text-[9px] text-slate-400">+{ep.tasks.length - 3}</span>}
                                    </div>
                                    {ep.already_batched > 0 && (
                                      <Badge className="text-[8px] bg-amber-50 text-amber-600 border border-amber-200 font-normal flex-shrink-0">
                                        <Lock className="w-2.5 h-2.5 mr-0.5" />
                                        {Object.entries(ep.batched_types || {}).map(([t, c]) => `${t}:${c}`).join(", ")}
                                      </Badge>
                                    )}
                                    <span className="text-xs text-slate-400 whitespace-nowrap">{ep.task_count} tasks</span>
                                    <span className="text-xs text-slate-600 whitespace-nowrap">{fmtFull(ep.total_amount)}</span>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                  {filteredTree.length === 0 && !loadingTree && (
                    <div className="text-center py-8 text-sm text-slate-400">No projects found</div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Fully Batched Projects — already locked, not in tree */}
          {fullyBatched.length > 0 && (
            <Card className="mt-4 border-amber-200" data-testid="fully-batched-card">
              <CardHeader className="py-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2 text-amber-700">
                  <Lock className="w-4 h-4" /> Already Batched Projects ({fullyBatched.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-slate-500 mb-3">These projects have all tasks locked in existing batches — no remaining tasks to include.</p>
                <div className="flex flex-wrap gap-2">
                  {fullyBatched.map((p, i) => (
                    <div key={i} className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                      <Lock className="w-3 h-3 text-amber-500 flex-shrink-0" />
                      <span className="text-[10px] font-medium text-amber-700">{p.main_title}</span>
                      <div className="flex items-center gap-1">
                        {Object.entries(p.batched_types || {}).map(([t, c]) => (
                          <Badge key={t} className="text-[8px] bg-amber-100 text-amber-600 border-0 font-normal">{t}: {c}</Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>)}
      </div>
    );
  }

  /* ============================== */
  /* REGULAR: LIST VIEW (default)   */
  /* ============================== */
  return (
    <div className="p-6 max-w-full overflow-hidden" data-testid="batch-list-view">
      <div className="flex items-center justify-between mb-2 gap-4 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-slate-900">Invoice Batches</h1>
          <p className="text-sm text-slate-500 mt-0.5">Monthly project selection for invoicing</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button variant="outline" size="sm" onClick={() => { fetchArchived(); setView("archived"); }} data-testid="view-archived-btn"><Archive className="w-4 h-4 mr-1" />Archived</Button>
          <Button variant="outline" size="sm" onClick={fetchAll} data-testid="refresh-batches-btn"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
          <Button size="sm" onClick={startCreate} data-testid="create-batch-btn"><Plus className="w-4 h-4 mr-1" />Create Batch</Button>
        </div>
      </div>
      <TabBar />
      {batches.length > 0 ? (
        <div className="space-y-3">
          {[...batches].sort((a, b) => (b.month || "").localeCompare(a.month || "")).map(b => {
            const isLocked = b.status === "locked";
            return (
              <Card key={b.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => { setSelectedBatch(b.id); fetchBatchDetail(b.id); setView("detail"); }} data-testid={`batch-card-${b.id}`}>
                <CardContent className="py-4"><div className="flex items-center justify-between"><div className="flex items-center gap-4"><div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isLocked ? "bg-emerald-100" : "bg-amber-100"}`}>{isLocked ? <Lock className="w-6 h-6 text-emerald-600" /> : <Clock className="w-6 h-6 text-amber-600" />}</div><div><div className="flex items-center gap-2 flex-wrap"><h3 className="text-base font-semibold text-slate-900">{b.name}</h3><Badge className={isLocked ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]" : "bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]"}>{isLocked ? "Locked" : "Draft"}</Badge>{b.billing_month && b.billing_month !== b.month && <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-50 text-[10px]">Billing: {fmtMonth(b.billing_month)}</Badge>}{b.season_list?.length > 0 && b.season_list.slice(0, 4).map(sn => <Badge key={sn} className="bg-violet-100 text-violet-700 hover:bg-violet-100 text-[10px]">S{sn}</Badge>)}{b.season_list?.length > 4 && <Badge className="bg-slate-100 text-slate-600 text-[10px]">+{b.season_list.length - 4} more seasons</Badge>}</div><p className="text-sm text-slate-500">DB: {fmtMonth(b.month)}{b.billing_month ? ` \u00B7 Billing: ${fmtMonth(b.billing_month)}` : ""}{b.episode_count ? ` \u00B7 ${b.episode_count} episode${b.episode_count > 1 ? "s" : ""}` : ""}</p></div></div><div className="flex items-center gap-6"><div className="text-right"><p className="text-sm text-slate-500">{b.task_count} tasks</p><p className="text-base font-bold text-slate-900">{fmt(b.total_amount || 0)}</p></div><ChevronRight className="w-5 h-5 text-slate-400" /></div></div></CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16">
          <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" /><p className="text-slate-500 font-medium">No invoice batches yet</p><p className="text-sm text-slate-400 mt-1">Create your first batch to start selecting projects for invoicing</p>
          <Button size="sm" className="mt-4" onClick={startCreate} data-testid="create-first-batch-btn"><Plus className="w-4 h-4 mr-1" />Create First Batch</Button>
        </div>
      )}
    </div>
  );
};

const Label = ({ children, ...props }) => <label {...props}>{children}</label>;

export default InvoiceBatches;
